<script lang="ts">
  import type { ApiField } from '../lib/data-tables-api';
  import Icon from './Icon.svelte';
  import FieldEditor from '../lib/fields/FieldEditor.svelte';

  let {
    open,
    selectedIds,
    editableFields,
    entityLabel = 'записів',
    onApply,
    onClose,
    onDone,
  }: {
    /** Чи відкритий modal. */
    open: boolean;
    /** UUID'и записів які буде оновлено. */
    selectedIds: string[];
    /** Поля доступні для bulk edit (вже відфільтровані caller'ом — i.e. без computed/linked). */
    editableFields: ApiField[];
    /** "контактів" / "платежів" / "записів" — для повідомлень. */
    entityLabel?: string;
    /** Callback per-id: викликається для кожного selected id з обраним fieldKey + value.
     *  Має сам відрізнити built-in vs custom за полем. Кидає Error на помилку. */
    onApply: (id: string, field: ApiField, value: unknown) => Promise<void>;
    /** Коли admin клікає Cancel / overlay. */
    onClose: () => void;
    /** Коли apply закінчився (success + errors) — caller робить reload + clearSelection.
     *  Передаємо progress для UX повідомлення. */
    onDone: (progress: { done: number; total: number; errors: number }) => void;
  } = $props();

  let fieldKey = $state<string>('');
  let value = $state<unknown>(undefined);
  let busy = $state(false);
  let progress = $state({ done: 0, total: 0, errors: 0 });

  const field = $derived(editableFields.find((f) => f.fieldKey === fieldKey) ?? null);

  // Сбрасуємо стан при кожному відкритті.
  let prevOpen = false;
  $effect(() => {
    if (open && !prevOpen) {
      fieldKey = '';
      value = undefined;
      progress = { done: 0, total: 0, errors: 0 };
    }
    prevOpen = open;
  });

  function tryClose() {
    if (busy) return;
    onClose();
  }

  async function apply() {
    if (!field || busy) return;
    const f = field;
    const v = value === '' || value === undefined ? null : value;
    busy = true;
    progress = { done: 0, total: selectedIds.length, errors: 0 };
    for (const id of selectedIds) {
      try {
        await onApply(id, f, v);
        progress = { ...progress, done: progress.done + 1 };
      } catch {
        progress = {
          ...progress,
          done: progress.done + 1,
          errors: progress.errors + 1,
        };
      }
    }
    busy = false;
    onDone(progress);
  }
</script>

{#if open}
  <div
    class="fixed inset-0 z-40 bg-black/40 flex items-center justify-center"
    onclick={tryClose}
    role="presentation"
  >
    <div
      class="w-[min(480px,90vw)] rounded-xl bg-white shadow-2xl p-5"
      onclick={(e) => e.stopPropagation()}
      role="dialog"
    >
      <div class="flex items-center gap-1.5 text-sm font-bold mb-1"><Icon name="edit" size={15} /> Масове редагування</div>
      <div class="text-xs text-[var(--color-text-muted)] mb-4">
        Застосувати одне значення до <strong>{selectedIds.length}</strong> {entityLabel}.
      </div>

      {#if editableFields.length === 0}
        <div class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 mb-3">
          Немає доступних полів для масового редагування. Створіть custom-поле у Settings → Структура даних.
        </div>
      {:else}
        <label class="block mb-3">
          <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Поле</div>
          <select
            bind:value={fieldKey}
            onchange={() => (value = undefined)}
            disabled={busy}
            class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm"
          >
            <option value="">— оберіть поле —</option>
            {#each editableFields as f (f.id)}
              <option value={f.fieldKey}>{f.label}{f.builtIn ? '' : ' (custom)'}</option>
            {/each}
          </select>
        </label>

        {#if field}
          <label class="block mb-4">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Нове значення</div>
            <FieldEditor
              {field}
              {value}
              onChange={(v) => (value = v)}
              disabled={busy}
            />
            <div class="mt-1 text-[10.5px] text-[var(--color-text-muted-2)]">
              Пусте значення → поле буде очищено (null).
            </div>
          </label>
        {/if}
      {/if}

      {#if busy}
        <div class="mb-3 rounded-md border border-[var(--color-pu)]/30 bg-[var(--color-pu-light)]/30 px-3 py-2 text-[11px]">
          Оновлено {progress.done} з {progress.total}{progress.errors > 0 ? ` · помилки: ${progress.errors}` : ''}
        </div>
      {/if}

      <div class="flex justify-end gap-2">
        <button
          onclick={tryClose}
          disabled={busy}
          class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-xs font-medium disabled:opacity-50"
        >Скасувати</button>
        <button
          onclick={apply}
          disabled={busy || !field}
          class="rounded-md btn-pu px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
        >{busy ? `Оновлюю… ${progress.done}/${progress.total}` : 'Застосувати'}</button>
      </div>
    </div>
  </div>
{/if}
